<template>
  <Dialog :open="!!isOpen" @update:open="(value: boolean) => !value && closeModal()">
    <DialogContent class="!max-w-[100vw] !w-full !h-screen !p-0 !gap-0 !overflow-auto !left-1/2 !top-1/2 !-translate-x-1/2 !-translate-y-1/2 !rounded-lg !shadow-none !max-h-none !border-none !bg-background/10 backdrop-blur-sm">
      <DialogTitle class="sr-only">{{ vehicleTitle || 'Vehicle Details' }}</DialogTitle>
      <DialogDescription class="sr-only">{{ vehicleDescriptionForAccessibility }}</DialogDescription>
      <DialogClose class="absolute right-6 top-6 rounded-full p-2 transition-colors z-[60]">

      </DialogClose>

      <div v-if="loading" class="flex justify-center items-center py-20">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>

      <template v-else-if="vehicle">
        <!-- Main Grid Layout - Full height with content-based sizing -->
        <div class="grid max-w-[98vw] xl:max-w-[88vw] 2xl:max-w-[60vw] m-auto lg:grid-cols-[minmax(400px,500px)_1fr] xl:grid-cols-[minmax(450px,550px)_1fr] h-screen overflow-auto">
          
          <!-- Left Sidebar - Vehicle Info & Actions -->
          <div class="min-w-0 flex flex-col">
            <div class="bg-card p-6 space-y-6 flex-1">
              
              <!-- Vehicle Header Card -->
              <div class="text-card-foreground rounded-lg border p-6 shadow-sm">
                <div class="pb-4">
                  <div class="flex items-start justify-between">
                    <div class="space-y-2 min-w-0 flex-1">
                      <div class="flex items-center gap-2">
                        <span v-if="vehicle.condition?.displayValue?.includes('New')" class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-transparent bg-red-600 text-white">
                          NEW
                        </span>
                        <span v-else-if="vehicle.condition?.displayValue?.includes('Used')" class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-transparent bg-secondary text-secondary-foreground">
                          USED
                        </span>
                        <span v-else-if="vehicle.condition?.displayValue?.includes('Demo')" class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-transparent bg-blue-600 text-white">
                          DEMO
                        </span>
                      </div>
                      <h3 class="text-2xl font-bold leading-tight tracking-tight break-words">
                        {{ vehicle.title }}
                      </h3>
                      <p class="text-sm text-muted-foreground">
                        {{ vehicle.make?.displayValue?.[0] }} {{ vehicle.model?.displayValue?.[0] }}
                      </p>
                    </div>
                    
                    <Button
                      @click.stop="toggleFavorite(vehicle.id)"
                      variant="outline"
                      size="sm"
                      class="shrink-0 ml-4"
                    >
                      <HeartIcon
                        :class="[
                          'h-4 w-4 transition-colors duration-300',
                          isFavorite(vehicle.id) ? 'text-red-500 fill-red-500' : 'text-muted-foreground'
                        ]"
                      />
                    </Button>
                  </div>
                </div>
                
                <div class="space-y-4">
                  <!-- Key Specs Grid -->
                  <div class="grid grid-cols-2 gap-3 text-sm">
                    <div v-if="vehicle.drivetrain?.displayValue?.[0]" class="flex items-center gap-2">
                      <GitForkIcon class="h-4 w-4 text-muted-foreground shrink-0" />
                      <span class="truncate">{{ vehicle.drivetrain.displayValue[0] }}</span>
                    </div>
                    <div v-if="vehicle.seats?.displayValue?.[0]" class="flex items-center gap-2">
                      <UsersIcon class="h-4 w-4 text-muted-foreground shrink-0" />
                      <span class="truncate">{{ vehicle.seats.displayValue[0] }} Seats</span>
                    </div>
                    <div v-if="vehicle.transmission?.displayValue?.[0]" class="flex items-center gap-2">
                      <SlidersHorizontalIcon class="h-4 w-4 text-muted-foreground shrink-0" />
                      <span class="truncate">{{ vehicle.transmission.displayValue[0] }}</span>
                    </div>
                    <div v-if="vehicle.kms" class="flex items-center gap-2">
                      <GaugeIcon class="h-4 w-4 text-muted-foreground shrink-0" />
                      <span class="truncate">{{ vehicle.kms.toLocaleString() }} km</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Pricing Card -->
              <div class="bg-card text-card-foreground rounded-lg border p-6 shadow-sm">
                <div class="pb-3 text-center">
                  <h3 class="text-lg font-semibold leading-none tracking-tight">Pricing</h3>
                </div>
                <div class="space-y-4">
                  <div class="text-center">
                    <div class="text-3xl font-bold text-primary mb-1">
                      {{ getVehiclePriceSafe(vehicle) }}*
                    </div>
                    <p class="text-xs text-muted-foreground uppercase tracking-wide">DRIVE AWAY</p>
                  </div>
                  
                  <!-- <div class="shrink-0 bg-border h-[1px] w-full"></div>
                  
                  <div v-if="vehicle.perweek || vehicle.price" class="text-center">
                    <div class="text-2xl font-bold mb-1">
                      ${{ formatPerWeek(vehicle.perweek) }}<span class="text-lg font-medium text-muted-foreground">/week</span>
                    </div>
                    <p class="text-xs text-muted-foreground">Estimated repayments</p>
                  </div> -->
                </div>
              </div>

              <!-- Contact Card -->
              <div class="bg-card text-card-foreground rounded-lg border p-6 shadow-sm">
                <div class="text-center">
                  <p class="text-sm font-medium text-muted-foreground mb-1">Call us today</p>
                  <p class="text-xl font-bold text-primary mb-2">(03) 9599 2155</p>
                  <p v-if="vehicle.stockid" class="text-xs text-muted-foreground">
                    Stock No: {{ vehicle.stockid }}
                  </p>
                </div>
              </div>

              <!-- Action Tabs -->
              <div class="bg-card text-card-foreground rounded-lg border p-6 shadow-sm">
                <div class="w-full">
                  <div class="inline-flex h-10 items-center justify-center rounded-md bg-muted p-1 text-muted-foreground w-full grid grid-cols-3">
                    <button 
                      @click="activeEnquiryTab = 'enquire'"
                      :class="[
                        'inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
                        activeEnquiryTab === 'enquire' ? 'bg-background text-foreground shadow-sm' : 'hover:bg-background/80'
                      ]"
                    >
                      Enquire
                    </button>
                    <button 
                      @click="activeEnquiryTab = 'calculator'"
                      :class="[
                        'inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
                        activeEnquiryTab === 'calculator' ? 'bg-background text-foreground shadow-sm' : 'hover:bg-background/80'
                      ]"
                    >
                      Calculator
                    </button>
                    <button 
                      @click="activeEnquiryTab = 'location'"
                      :class="[
                        'inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
                        activeEnquiryTab === 'location' ? 'bg-background text-foreground shadow-sm' : 'hover:bg-background/80'
                      ]"
                    >
                      Location
                    </button>
                  </div>
                  
                  <div v-if="activeEnquiryTab === 'enquire'" class="mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                    <div class="pt-6">
                      <div class="space-y-4">
                        <div>
                          <h4 class="font-semibold mb-2">Get in touch with us today</h4>
                          <p class="text-sm text-muted-foreground mb-4">
                            We'd love to help you discover if this {{ vehicle.make?.displayValue?.[0] }} {{ vehicle.model?.displayValue?.[0] }} is perfect for you!
                          </p>
                        </div>
                        <div>
                          <VehicleEnquiryForm
                            ref="enquiryForm"
                            :vehicle="(vehicle || { id: '', price: 0 }) as any"
                            @submit="handleEnquirySubmit"
                            :show-reserve-option="true"
                            :show-trade-in-option="true"
                            :show-finance-option="true"
                            :show-test-drive-option="true"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div v-if="activeEnquiryTab === 'calculator'" class="mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                    <div class="pt-6">
                      <div class="text-center py-8">
                        <p class="text-muted-foreground">Calculator feature coming soon.</p>
                      </div>
                    </div>
                  </div>
                  
                  <div v-if="activeEnquiryTab === 'location'" class="mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                    <div class="pt-6">
                      <div class="text-center py-8">
                        <p class="text-muted-foreground">Location feature coming soon.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Right Main Content - Gallery & Details -->
          <div class="min-w-0 flex flex-col">
            <div class="bg-card p-6 space-y-6 flex-1">
              
              <!-- Navigation Cards -->
              <div v-if="hasPrevious || hasNext" class="grid grid-cols-1 grid-cols-2 gap-4">
                                 <!-- Previous Vehicle -->
                 <div
                   v-if="hasPrevious && previousVehicle"
                   class="bg-card text-card-foreground rounded-lg border p-4 shadow-sm cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-[1.02] border-muted"
                   @click="navigateToPrevious"
                 >
                   <div class="flex items-center gap-3">
                     <div class="h-18 w-24 rounded-md overflow-hidden shrink-0">
                       <img
                         :src="previousVehicle?.thumb || (previousVehicle?.photos?.length ? previousVehicle.photos[0] : 'https://placehold.co/300x225?text=No+Image')"
                         :alt="getVehicleTitle(previousVehicle)"
                         class="w-full h-full object-cover"
                       />
                     </div>
                     <div class="flex-1 min-w-0">
                       <div class="flex items-center gap-1 mb-1">
                         <ChevronLeftIcon class="h-4 w-4 text-muted-foreground shrink-0" />
                         <span class="text-xs font-medium text-muted-foreground">Previous</span>
                       </div>
                       <h4 class="text-sm font-medium truncate">{{ previousVehicleData?.title }}</h4>
                       <p class="text-sm font-semibold text-primary">{{ previousVehicleData?.price }}</p>
                     </div>
                   </div>
                 </div>

                                 <!-- Next Vehicle -->
                 <div
                   v-if="hasNext && nextVehicle"
                   class="bg-card text-card-foreground rounded-lg border p-4 shadow-sm cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-[1.02] border-muted"
                   @click="navigateToNext"
                 >
                   <div class="flex items-center gap-3">
                     <div class="flex-1 min-w-0 text-right">
                       <div class="flex items-center justify-end gap-1 mb-1">
                         <span class="text-xs font-medium text-muted-foreground">Next</span>
                         <ChevronRightIcon class="h-4 w-4 text-muted-foreground shrink-0" />
                       </div>
                       <h4 class="text-sm font-medium truncate">{{ nextVehicleData?.title }}</h4>
                       <p class="text-sm font-semibold text-primary">{{ nextVehicleData?.price }}</p>
                     </div>
                     <div class="h-18 w-24 rounded-md overflow-hidden shrink-0">
                       <img
                         :src="nextVehicle?.thumb || (nextVehicle?.photos?.length ? nextVehicle.photos[0] : 'https://placehold.co/300x225?text=No+Image')"
                         :alt="getVehicleTitle(nextVehicle)"
                         class="w-full h-full object-cover"
                       />
                     </div>
                   </div>
                 </div>
              </div>

                             <!-- Gallery Card -->
               <div class="">
                 <div class="relative">
                   <VehicleGallery
                     :photos="vehicle?.photos || [vehicle?.thumb || 'https://placehold.co/600x400?text=No+Image']"
                     :title="vehicleTitle"
                     :initial-index="currentImageIndex"
                     class="rounded-lg aspect-[4/3]"
                   />
                   
                   <!-- Condition Badge on Gallery -->
                   <div v-if="vehicle?.condition?.displayValue?.length && !vehicle.condition.displayValue.includes('New') && !vehicle.condition.displayValue.includes('Used') && !vehicle.condition.displayValue.includes('Demo')" 
                        class="absolute top-4 left-4 z-10">
                     <span class="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold text-foreground bg-background/80 backdrop-blur-sm">
                       {{ vehicle.condition.displayValue.join(', ') }}
                     </span>
                   </div>
                 </div>
               </div>

                             <!-- Comments Card -->
               <div class="bg-card text-card-foreground rounded-lg border p-6 shadow-sm">
                 <div class="flex flex-col space-y-1.5 pb-6">
                   <h3 class="text-xl font-semibold leading-none tracking-tight">Comments</h3>
                 </div>
                 <div class="space-y-4">
                   <div v-html="displayedCommentHtml" class="prose prose-sm max-w-none text-foreground leading-relaxed"></div>
                   <Button 
                     v-if="shouldShowReadMoreButton" 
                     @click="isCommentExpanded = !isCommentExpanded"
                     variant="outline" 
                     class="w-full"
                   >
                     {{ isCommentExpanded ? 'Read Less' : 'Read More' }}
                   </Button>
                 </div>
               </div>

                             <!-- Vehicle Details Card -->
               <div class="bg-card text-card-foreground rounded-lg border p-6 shadow-sm">
                 <div class="flex flex-col space-y-1.5 pb-6">
                   <h3 class="text-xl font-semibold leading-none tracking-tight">Vehicle Details</h3>
                   <p class="text-sm text-muted-foreground">{{ vehicle.title }}</p>
                 </div>
                 <div class="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                   <div class="flex justify-between">
                     <span class="text-muted-foreground">Year:</span> 
                     <span class="font-medium">{{ vehicle.year || 'N/A' }}</span>
                   </div>
                   <div class="flex justify-between">
                     <span class="text-muted-foreground">Odometer:</span> 
                     <span class="font-medium">{{ vehicle.kms ? vehicle.kms.toLocaleString() + ' km' : 'N/A' }}</span>
                   </div>
                   <div class="flex justify-between">
                     <span class="text-muted-foreground">Condition:</span> 
                     <span class="font-medium">{{ vehicle.condition?.displayValue?.join(', ') || 'N/A' }}</span>
                   </div>
                   <div class="flex justify-between">
                     <span class="text-muted-foreground">Registration:</span> 
                     <span class="font-medium">{{ vehicle.rego || 'Unregistered' }}</span>
                   </div>
                   <div class="flex justify-between">
                     <span class="text-muted-foreground">Reg. Expiry:</span> 
                     <span class="font-medium">{{ vehicle.regoexpiry || 'N/A' }}</span>
                   </div>
                   <div class="flex justify-between">
                     <span class="text-muted-foreground">Make:</span> 
                     <span class="font-medium">{{ vehicle.make?.displayValue?.[0] || 'N/A' }}</span>
                   </div>
                   <div class="flex justify-between">
                     <span class="text-muted-foreground">Model:</span> 
                     <span class="font-medium">{{ vehicle.model?.displayValue?.[0] || 'N/A' }}</span>
                   </div>
                   <div class="flex justify-between">
                     <span class="text-muted-foreground">Badge:</span> 
                     <span class="font-medium">{{ vehicle.badge?.displayValue?.[0] || 'N/A' }}</span>
                   </div>
                   <div class="flex justify-between">
                     <span class="text-muted-foreground">Series:</span> 
                     <span class="font-medium">{{ vehicle.series?.displayValue?.[0] || 'N/A' }}</span>
                   </div>
                   <div class="flex justify-between">
                     <span class="text-muted-foreground">Body Type:</span> 
                     <span class="font-medium">{{ vehicle.body?.displayValue?.[0] || 'N/A' }}</span>
                   </div>
                   <div class="flex justify-between">
                     <span class="text-muted-foreground">Ext. Colour:</span> 
                     <span class="font-medium">{{ vehicle.colour?.displayValue?.[0] || 'N/A' }}</span>
                   </div>
                   <div class="flex justify-between">
                     <span class="text-muted-foreground">Doors:</span> 
                     <span class="font-medium">{{ vehicle.doors?.displayValue?.[0] || 'N/A' }}</span>
                   </div>
                   <div class="flex justify-between">
                     <span class="text-muted-foreground">Seats:</span> 
                     <span class="font-medium">{{ vehicle.seats?.displayValue?.[0] ? vehicle.seats.displayValue[0] + ' Seats' : 'N/A' }}</span>
                   </div>
                   <div class="flex justify-between">
                     <span class="text-muted-foreground">Int. Colour:</span> 
                     <span class="font-medium">{{ vehicle.interiorcolour || 'N/A' }}</span>
                   </div>
                   <div class="flex justify-between col-span-1 md:col-span-2">
                     <span class="text-muted-foreground">Engine:</span> 
                     <span class="font-medium text-right">{{ vehicle.engineDescription || 'N/A' }}</span>
                   </div>
                   <div class="flex justify-between">
                     <span class="text-muted-foreground">Fuel Type:</span> 
                     <span class="font-medium">{{ vehicle.fuel?.displayValue?.[0] || 'N/A' }}</span>
                   </div>
                   <div class="flex justify-between">
                     <span class="text-muted-foreground">Gears:</span> 
                     <span class="font-medium">{{ vehicle.gears || 'N/A' }}</span>
                   </div>
                   <div class="flex justify-between">
                     <span class="text-muted-foreground">Transmission:</span> 
                     <span class="font-medium">{{ vehicle.transmission?.displayValue?.[0] || 'N/A' }}</span>
                   </div>
                   <div class="flex justify-between">
                     <span class="text-muted-foreground">VIN:</span> 
                     <span class="font-medium">{{ vehicle.vin || 'N/A' }}</span>
                   </div>
                   <div class="flex justify-between">
                     <span class="text-muted-foreground">Stock No:</span> 
                     <span class="font-medium">{{ vehicle.stockid || 'N/A' }}</span>
                   </div>
                 </div>
               </div>
            </div>
          </div>
        </div>

      </template>

      <div v-else class="flex justify-center items-center py-20">
        <div class="bg-card text-card-foreground rounded-lg border p-8 shadow-sm text-center">
          <p class="text-muted-foreground mb-4">Vehicle not found or failed to load.</p>
          <Button as-child>
            <NuxtLink to="/vehicles">
              View All Vehicles
            </NuxtLink>
          </Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { useVehiclesStore } from '~/stores/vehicles'
import { useFavoritesStore } from '~/stores/favorites'
import VehicleGallery from '~/layers/base/components/VehicleGallery.vue'
import { Dialog, DialogContent, DialogClose, DialogTitle, DialogDescription } from '~/layers/base/components/ui/dialog'
import { Button } from '~/layers/base/components/ui/button'
import { X as XIcon, Heart as HeartIcon, GitForkIcon, UsersIcon, SlidersHorizontalIcon, GaugeIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-vue-next'
import type { PropType } from 'vue'
import type { Vehicle } from '../../../types'
import { useRoute, useRouter } from '#imports'
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { calculateWeeklyPayment } from '~/layers/base/utils/finance'
import VehicleEnquiryForm from '~/layers/base/components/form/VehicleEnquiryForm.vue';

const activeEnquiryTab = ref<string>('enquire');

const formatPerWeek = (perweekInput: number | { value: string[] | number[] } | undefined): string => {
  let perweek = perweekInput;
  if (perweek === undefined && vehicle.value?.price) {
    const priceAsNumber = Number(vehicle.value.price ?? 0);
    if (priceAsNumber > 0) {
      const calculated = calculateWeeklyPayment(priceAsNumber);
      return Math.round(calculated).toString();
    }
    // If perweek is undefined and price is not valid for calculation, use original undefined perweek for below checks
  } else if (typeof perweek === 'object' && perweek !== null && 'value' in perweek && Array.isArray(perweek.value)) {
    const value = perweek.value[0];
    if (typeof value === 'number') {
      perweek = value;
    } else if (typeof value === 'string') {
      perweek = parseFloat(value);
    }
  }

  if (typeof perweek === 'number' && !isNaN(perweek) && perweek > 0) {
    return Math.round(perweek).toString();
  }
  
  return '0'; // Default if no valid perweek found or calculated
}

const getVehicleTitle = (vehicleInstance: Vehicle | null) => {
  if (!vehicleInstance) return ''
  if (vehicleInstance.title) return vehicleInstance.title
  if (vehicleInstance.make?.displayValue?.[0] && vehicleInstance.model?.displayValue?.[0]) {
    return `${vehicleInstance.make.displayValue[0]} ${vehicleInstance.model.displayValue[0]}`
  }
  return 'Unknown Vehicle'
}

const formatPrice = (price: number | string | undefined): string => {
  if (price === undefined) return '$0'
  const numericPrice = typeof price === 'string' ? parseFloat(price.replace(/[^0-9.]/g, '')) : price;
  if (isNaN(numericPrice)) return '$0'
  return numericPrice.toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    currencyDisplay: 'symbol'
  }).replace('A$', '$')
}

const getVehiclePriceSafe = (vehicleInstance: Vehicle | null): string => {
  if (!vehicleInstance?.price) return '$0'
  return formatPrice(vehicleInstance.price)
}

const getVehicleDetails = (vehicleInstance: Vehicle | null) => {
  if (!vehicleInstance) return []
  return [
    vehicleInstance.year,
    vehicleInstance.make?.displayValue?.[0],
    vehicleInstance.model?.displayValue?.[0]
  ].filter(Boolean)
}

const previousVehicle = ref<Vehicle | null>(null)
const nextVehicle = ref<Vehicle | null>(null)

const props = defineProps({
  vehicleId: {
    type: String,
    required: false,
    default: undefined
  },
  isOpen: {
    type: Boolean,
    required: true
  },
  filteredVehicleIds: {
    type: Array as PropType<string[]>,
    default: () => []
  }
})

const emit = defineEmits(['update:isOpen', 'close', 'navigate'])

const vehiclesStore = useVehiclesStore()
const favoritesStore = useFavoritesStore()
const loading = ref(true)
const error = ref<string | null>(null)
const vehicle = ref<Vehicle | null>(null)
const currentImageIndex = ref(0)
const showEnquiryForm = ref(true)

const isCommentExpanded = ref(false);

const fallbackCommentText = computed(() => `Conquer Every Journey with the ${vehicle.value?.title || 'vehicle'}. This vehicle takes adventure to the next level with its rugged capabilities, advanced technology, and premium features. Built for those who demand power, efficiency, and luxury, this SUV is ready to tackle any terrain in style.`);

const plainFullCommentText = computed(() => {
  if (!vehicle.value) return '';
  const mainComment = vehicle.value.comments || fallbackCommentText.value;
  const keySpecs = vehicle.value.keySpecifications ? `\n\nKey Specifications:\n${vehicle.value.keySpecifications}` : '';
  return mainComment + keySpecs;
});

const COMMENT_LENGTH_THRESHOLD = 280; // Applied to plain text

const shouldShowReadMoreButton = computed(() => {
  return plainFullCommentText.value.length > COMMENT_LENGTH_THRESHOLD;
});

// Helper to convert plain text with newlines to HTML with <br>
const textToHtml = (text: string | undefined | null): string => {
  if (!text) return '';
  return text.replace(/\r\n|\r|\n/g, '<br />');
};

const displayedCommentHtml = computed(() => {
  if (!vehicle.value) return '';

  if (!shouldShowReadMoreButton.value || isCommentExpanded.value) {
    // Show full content with proper HTML structure
    const mainHtml = textToHtml(vehicle.value.comments || fallbackCommentText.value);
    let specsHtml = '';
    if (vehicle.value.keySpecifications) {
      specsHtml = `<br /><br /><strong>Key Specifications:</strong><br />${textToHtml(vehicle.value.keySpecifications)}`;
    }
    return mainHtml + specsHtml;
  } else {
    // Show truncated plain text converted to HTML
    const truncatedText = plainFullCommentText.value.substring(0, COMMENT_LENGTH_THRESHOLD) + '...';
    return textToHtml(truncatedText);
  }
});

const fetchPreviousNextVehicles = async () => {
  if (!vehicle.value) return

  const currentIdxInFiltered = props.filteredVehicleIds.findIndex(id => id === vehicle.value!.id);

  let prevId: string | null = null;
  let nextId: string | null = null;

  if (currentIdxInFiltered !== -1) {
    if (currentIdxInFiltered > 0) {
      prevId = props.filteredVehicleIds[currentIdxInFiltered - 1] || null;
    }
    if (currentIdxInFiltered < props.filteredVehicleIds.length - 1) {
      nextId = props.filteredVehicleIds[currentIdxInFiltered + 1] || null;
    }
  } else {
    // Fallback to store order if not in filtered list (e.g., direct link)
    const allVehicles = vehiclesStore.vehicles as Vehicle[]; // Cast for type safety
    const currentIndexInStore = allVehicles.findIndex(v => v.id === vehicle.value?.id)
    if (currentIndexInStore > 0 && allVehicles[currentIndexInStore - 1]) {
      prevId = allVehicles[currentIndexInStore - 1]?.id || null;
    }
    if (currentIndexInStore < allVehicles.length - 1 && allVehicles[currentIndexInStore + 1]) {
      nextId = allVehicles[currentIndexInStore + 1]?.id || null;
    }
  }

  try {
    previousVehicle.value = null
    nextVehicle.value = null
    // Fetch concurrently
    const fetchPromises = []
    if (prevId) {
      fetchPromises.push(
        vehiclesStore.fetchVehicleById(prevId).then(data => {
          if (data) previousVehicle.value = data
        })
      )
    }
    if (nextId) {
      fetchPromises.push(
        vehiclesStore.fetchVehicleById(nextId).then(data => {
          if (data) nextVehicle.value = data
        })
      )
    }
    await Promise.all(fetchPromises)
  } catch (err) {
    console.error('[VehicleQuickViewDesktop] Error fetching previous/next vehicles:', err)
    // Do not set main error state, as this is for auxiliary thumbs
  }
}

onMounted(() => {
  const handlePopState = () => {
    if (props.isOpen) closeModal()
  }
  window.addEventListener('popstate', handlePopState)
  onBeforeUnmount(() => window.removeEventListener('popstate', handlePopState))
})

const vehicleTitle = computed(() => getVehicleTitle(vehicle.value))

const vehicleDescriptionForAccessibility = computed(() => {
  if (!vehicle.value) return 'Vehicle details loading or not available.';
  
  const parts = [];
  if (vehicle.value.year) parts.push(String(vehicle.value.year));
  parts.push(getVehicleTitle(vehicle.value));

  if (vehicle.value.kms) parts.push(`Odometer: ${vehicle.value.kms.toLocaleString()} km.`);
  if (vehicle.value.transmission?.displayValue?.[0]) parts.push(`Transmission: ${vehicle.value.transmission.displayValue[0]}.`);
  if (vehicle.value.fuel?.displayValue?.[0]) parts.push(`Fuel: ${vehicle.value.fuel.displayValue[0]}.`);
  if (vehicle.value.drivetrain?.displayValue?.[0]) parts.push(`Drive: ${vehicle.value.drivetrain.displayValue[0]}.`);
  if (vehicle.value.seats) parts.push(`${vehicle.value.seats} seats.`);
  parts.push(`Priced at ${getVehiclePriceSafe(vehicle.value)}.`);
  
  return parts.filter(Boolean).join(' ');
});

const currentIndex = computed(() => {
  if (!props.filteredVehicleIds.length || !props.vehicleId) {
    // If no filtered list or current ID, try to find in the main store
    if (vehicle.value) {
        const allVehicles = vehiclesStore.vehicles as Vehicle[];
        return allVehicles.findIndex(v => v.id === vehicle.value!.id);
    }
    return -1;
  }
  return props.filteredVehicleIds.findIndex(id => id === props.vehicleId)
})

const hasPrevious = computed(() => {
    if (props.filteredVehicleIds.length > 0 && props.vehicleId) {
        return props.filteredVehicleIds.findIndex(id => id === props.vehicleId) > 0;
    }
    // Fallback for when filteredVehicleIds might not be relevant (e.g. direct link)
    if (vehicle.value) {
        const allVehicles = vehiclesStore.vehicles as Vehicle[];
        const idx = allVehicles.findIndex(v => v.id === vehicle.value!.id);
        return idx > 0;
    }
    return false;
})

const hasNext = computed(() => {
    if (props.filteredVehicleIds.length > 0 && props.vehicleId) {
        const idx = props.filteredVehicleIds.findIndex(id => id === props.vehicleId);
        return idx !== -1 && idx < props.filteredVehicleIds.length - 1;
    }
    // Fallback
    if (vehicle.value) {
        const allVehicles = vehiclesStore.vehicles as Vehicle[];
        const idx = allVehicles.findIndex(v => v.id === vehicle.value!.id);
        return idx !== -1 && idx < allVehicles.length - 1;
    }
    return false;
})

const previousVehicleId = computed(() => {
    if (hasPrevious.value) {
        if (props.filteredVehicleIds.length > 0 && props.vehicleId) {
            const currentIdxInFiltered = props.filteredVehicleIds.findIndex(id => id === props.vehicleId);
            if (currentIdxInFiltered > 0) return props.filteredVehicleIds[currentIdxInFiltered - 1];
        }
        if (vehicle.value) {
            const allVehicles = vehiclesStore.vehicles as Vehicle[];
            const currentIndexInStore = allVehicles.findIndex(v => v.id === vehicle.value!.id);
            if (currentIndexInStore > 0 && allVehicles[currentIndexInStore - 1]) return allVehicles[currentIndexInStore - 1]?.id;
        }
    }
    return null;
})

const nextVehicleId = computed(() => {
    if (hasNext.value) {
        if (props.filteredVehicleIds.length > 0 && props.vehicleId) {
            const currentIdxInFiltered = props.filteredVehicleIds.findIndex(id => id === props.vehicleId);
            if (currentIdxInFiltered !== -1 && currentIdxInFiltered < props.filteredVehicleIds.length - 1) {
                return props.filteredVehicleIds[currentIdxInFiltered + 1];
            }
        }
        if (vehicle.value) {
            const allVehicles = vehiclesStore.vehicles as Vehicle[];
            const currentIndexInStore = allVehicles.findIndex(v => v.id === vehicle.value!.id);
            if (currentIndexInStore !== -1 && currentIndexInStore < allVehicles.length - 1 && allVehicles[currentIndexInStore + 1]) {
                return allVehicles[currentIndexInStore + 1]?.id;
            }
        }
    }
    return null;
})

const navigateToPrevious = () => {
  if (previousVehicleId.value) emit('navigate', previousVehicleId.value)
}

const navigateToNext = () => {
  if (nextVehicleId.value) emit('navigate', nextVehicleId.value)
}

const closeModal = () => {
  emit('close');
  activeEnquiryTab.value = 'enquire';
  showEnquiryForm.value = true;
}

const fetchVehicleData = async (id: string, forceRefresh = false) => {
  if (!id) return

  loading.value = true
  error.value = null
  currentImageIndex.value = 0 
  activeEnquiryTab.value = 'enquire'; 
  showEnquiryForm.value = true;

  try {
    let fetchedVehicleData: Vehicle | null = null;
    if (!forceRefresh) {
      fetchedVehicleData = (vehiclesStore.vehicles as Vehicle[]).find(v => v.id === id || v.stockid === id) || null;
    }

    if (fetchedVehicleData) {
      vehicle.value = fetchedVehicleData
    } else {
      console.log(`[VehicleQuickViewDesktop] Fetching from API: ${id}`)
      fetchedVehicleData = await vehiclesStore.fetchVehicleById(id);
      if (fetchedVehicleData) {
        vehicle.value = fetchedVehicleData
      } else {
        console.warn(`[VehicleQuickViewDesktop] Vehicle not found: ${id}`)
        error.value = 'Vehicle not found'
        vehicle.value = null;
      }
    }
  } catch (err: any) {
    console.error('[VehicleQuickViewDesktop] Error fetching vehicle:', err)
    if (err.name === 'AbortError') error.value = 'Request timed out.'
    else if (err.message?.includes('network')) error.value = 'Network error.'
    else if (err.message?.includes('404')) error.value = 'Vehicle not found'
    else error.value = 'Failed to load details.'
    vehicle.value = null;
  } finally {
    loading.value = false
    // Fetch prev/next after current vehicle is loaded (or failed to load but loading is false)
    if (vehicle.value || error.value) { // Ensure we attempt even if main vehicle failed, to show nav if possible
        fetchPreviousNextVehicles();
    }
  }
}

watch(() => [props.vehicleId, props.isOpen], ([newId, newIsOpen]) => {
  if (newIsOpen && newId) {
    fetchVehicleData(String(newId))
  } else if (!newIsOpen) {
    // vehicle.value = null;
  }
}, { immediate: true })

watch(vehicle, (newVehicleValue) => {
  if (newVehicleValue && props.isOpen) {
    fetchPreviousNextVehicles();
  }
}, { deep: true })

watch(() => props.filteredVehicleIds, () => {
  // This watch is crucial if the list of navigable vehicles changes while the dialog is open.
  if (vehicle.value && props.isOpen) {
    fetchPreviousNextVehicles()
  }
}, { deep: true })

const enquiryForm = ref<{ submitForm: () => void } | null>(null);
const handleEnquirySubmit = () => {
  // Manage post-submission state here
}
const submitEnquiry = () => { if (enquiryForm.value) enquiryForm.value.submitForm() }

const previousVehicleData = computed(() => previousVehicle.value ? { 
  title: getVehicleTitle(previousVehicle.value), 
  price: getVehiclePriceSafe(previousVehicle.value), 
  details: getVehicleDetails(previousVehicle.value).join(' ') 
} : null)

const nextVehicleData = computed(() => nextVehicle.value ? { 
  title: getVehicleTitle(nextVehicle.value), 
  price: getVehiclePriceSafe(nextVehicle.value), 
  details: getVehicleDetails(nextVehicle.value).join(' ') 
} : null)

const errorMessage = computed(() => error.value)

const isFavorite = (id: string | undefined): boolean => id ? favoritesStore.isFavorite(id) : false;
const toggleFavorite = (id: string | undefined) => { if (id) favoritesStore.toggleFavorite(id); }

defineExpose({
  refreshVehicleData: async () => {
    if (vehicle.value?.id) await fetchVehicleData(vehicle.value.id, true);
  }
});

</script>
